import React, { useEffect, useState } from 'react';
import { ASSIGNED_SAFETY_MEETINGS_COLUMN, QUERY_KEYS } from '../../../constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import { usePagination } from '../../../hooks';
import { resetSafetyMeetingStore } from '../../../redux';
import { popup } from '../../../utils';
import { Table } from '../Table';
import { Delete, SearchIcon } from '../../../assets/svg';
import {
    deleteSafetyMeetingAssociationService,
    getSafetyServiceAssociationByAccountIdService,
} from '../../../utils/apiServices/safetyServiceAPI';
import { Modal } from 'react-bootstrap';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import YearMonthPopoverPicker from '../common/YearMonthPopoverPicker';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

const AssignedSafetyMeetingModal = ({ showModal, setShowModal, accountId, accountName }) => {
    const queryClient = useQueryClient();
    const [safetyMeetings, setSafetyMeetings] = useState({ data: [], count: 0 });
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const selectedSafetyMeetings = useSelector((s) => s.safetyMeetings.selectedSafetyMeetings);
    const [search, setSearch] = useState('');
    const [dateFilter, setDateFilter] = useState({ month: '', year: '' });
    const dispatch = useDispatch();
    const pagination = usePagination(safetyMeetings?.data.length, safetyMeetings?.count, 50);

    const [debouncedSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, isFetched, isError, error, refetch } = useQuery({
        queryKey: [
            QUERY_KEYS.SAFETY_MEETING_ASSOCIATION_BY_ID,
            accountId,
            debouncedSearch,
            pagination.limit,
            pagination.offset,
            dateFilter.year,
            dateFilter.month,
        ],
        queryFn: async () =>
            await getSafetyServiceAssociationByAccountIdService({
                accountId: accountId,
                search: debouncedSearch,
                offset: pagination.offset,
                limit: pagination.limit,
                year: dateFilter.year,
                month: dateFilter.month,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
        enabled: showModal,
    });

    if (isError) popup('error', error.message);

    useEffect(() => {
        if (data && isFetched) {
            setSafetyMeetings(data);
        }
    }, [data, isFetched]);

    const deleteSMMutation = useMutation({
        mutationFn: async (data) => await deleteSafetyMeetingAssociationService(data),
        onSuccess: ({ message }) => {
            popup('success', message);
            setIsDeleteModalOpen(false);
            dispatch(resetSafetyMeetingStore());
            refetch();
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.SAFETY_MEETING_ASSOCIATION],
                exact: false,
            });
        },
        onError: (error) => popup('error', error.message),
    });

    const deleteHandler = async ({ selectedItemId }) => {
        const data = {
            accountId: selectedItemId,
            ids: selectedSafetyMeetings.map((item) => item.id),
        };
        deleteSMMutation.mutate(data);
    };

    return (
        <Modal
            show={showModal}
            onHide={() => {
                dispatch(resetSafetyMeetingStore());
                setShowModal(false);
            }}
            size='xl'
            dialogClassName='overflow-hidden'
            fullscreen='lg-down'
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title className='me-2'>{`${accountName}'s Safety Meetings`}</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '720px', overflow: 'hidden' }}>
                <div className='align-items-center d-flex flex-wrap flex-sm-nowrap justify-content-between gap-2 col-12 mb-1'>
                    <div
                        className=' w-100 custom-search-box d-flex align-items-center justify-content-start'
                        style={{ height: '48px', marginTop: '0.5rem' }}
                    >
                        <button className='bg-white p-0 border-0 d-flex align-items-center'>
                            <SearchIcon />
                        </button>
                        <input
                            type='text'
                            maxLength={40}
                            className='search-area border-0 w-100 py-1'
                            placeholder={'Search Safety Meetings...'}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className='w-100 d-flex justify-content-end'>
                        <YearMonthPopoverPicker
                            onChange={(v) => {
                                if (v) {
                                    const [month, year] = v.split('/');
                                    setDateFilter({ month, year });
                                } else {
                                    setDateFilter({ month: '', year: '' });
                                }
                            }}
                            className='mt-2'
                            selectStyle={{ width: '120px' }}
                            label={'Assigned Month/Year:'}
                            title={'Filter Meetings By Month/Year'}
                        />
                    </div>
                </div>

                {!!safetyMeetings && (
                    <Table
                        columns={ASSIGNED_SAFETY_MEETINGS_COLUMN}
                        data={safetyMeetings?.data}
                        pagination={pagination}
                        tableStyle={{ height: '54dvh', overflow: 'auto' }}
                        pageName='safetyMeeting(s)'
                        stickyColumns={[0, 'File Name']}
                        isLoading={isFetching}
                    />
                )}
            </Modal.Body>
            <Modal.Footer style={{minHeight : '3.8rem'}}>
                {selectedSafetyMeetings.length > 0 && (
                    <div
                        className={`w-100 d-flex align-items-center ${
                            selectedSafetyMeetings.length < 2
                                ? 'justify-content-end'
                                : 'justify-content-apart'
                        }`}
                    >
                        <div className=' d-flex w-100 justify-content-center align-items-center'>
                            <span
                                title={selectedSafetyMeetings.length > 1 ? 'Delete all' : 'Delete'}
                                id='action-btn'
                                onClick={() => setIsDeleteModalOpen(true)}
                                style={{ scale: '1.4' }}
                            >
                                <Delete />
                            </span>
                        </div>
                    </div>
                )}
            </Modal.Footer>
            {isDeleteModalOpen && (
                <DeleteConfirmationModal
                    isDeleteModal={isDeleteModalOpen}
                    setIsDeleteModal={setIsDeleteModalOpen}
                    selectedItemId={accountId}
                    handleDeleteClick={deleteHandler}
                    deleteButtonName={`${selectedSafetyMeetings.length} Safety Meetings of ${accountName}`}
                    isLoading={deleteSMMutation.isPending}
                />
            )}
        </Modal>
    );
};

export default AssignedSafetyMeetingModal;
